import React, { useRef, useState } from "react";
import { ClaimCompanyForm } from "./ClaimCompanyForm";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { track } from "api/analytics";
import { useDispatch } from "react-redux";
import { useProjectByCompanyName, useUserProfile } from "hooks";
import { sendMessage } from "state/websocket/operations";
import { CLAIM_COMPANY_FORM_VERIFICATION_STARTED } from "api/analytics/events";
import type { RequestEntities } from "types/charliui";
import { useToast } from "@chakra-ui/react";
import { ToastMessageContent } from "screens/common/components";

interface ClaimCompanyFormData {
  legalBusinessName: string;
  country: string;
  streetAddress: string;
  provinceState: string;
  postalZip: string;
  registrationTaxId: string;
  websiteUrl: string;
  crunchbasePitchbookUrl?: string;
  linkedinFacebookUrl?: string;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  acceptTerms: boolean;
}

interface RegistrationState {
  isCheckingAccount: boolean;
  isRegistered: boolean;
  hasCompletedStep2: boolean;
  hasCompletedStep3: boolean;
  isVerifying?: boolean;
  isCreatingProject: boolean;
  loadingText: string;
  hasReceivedResponse: boolean;
}

interface ClaimCompanyProps {
  setState: React.Dispatch<React.SetStateAction<RegistrationState>>;
}

export const ClaimCompany = ({ setState }: ClaimCompanyProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const conversationId = useRef(uuid());
  const params = useParams<{ symbol?: string; exchange?: string }>();
  const symbol = params.symbol;
  const exchange = params.exchange;
  const dispatch = useDispatch();
  const toast = useToast();
  const { email, fullName } = useUserProfile();
  const navigate = useNavigate();

  // default portfolio for IR Dashboard
  const portfolioId = useProjectByCompanyName("My Companies");

  const handleSubmit = async (formData: ClaimCompanyFormData) => {
    setIsVerifying(true);
    setSubmitError("");

    // Update registration state to show verification in progress
    setState((prev) => ({ ...prev, isVerifying: true }));

    try {
      setState((prev) => ({ ...prev, isVerifying: true, loadingText: "Submitting Claim..." }));
      const entities: RequestEntities = [];
      symbol && entities.push({ entity: "company_ticker", value: symbol });
      exchange && entities.push({ entity: "company_stock_exchange", value: exchange });
      // Add all form data as entities
      entities.push({ entity: "legal_business_name", value: formData.legalBusinessName });
      entities.push({ entity: "country", value: formData.country });
      entities.push({ entity: "street_address", value: formData.streetAddress });
      entities.push({ entity: "province_state", value: formData.provinceState });
      entities.push({ entity: "postal_zip", value: formData.postalZip });
      entities.push({ entity: "registration_tax_id", value: formData.registrationTaxId });
      entities.push({ entity: "website_url", value: formData.websiteUrl });
      formData.crunchbasePitchbookUrl && entities.push({ entity: "crunchbase_pitchbook_url", value: formData.crunchbasePitchbookUrl });
      formData.linkedinFacebookUrl && entities.push({ entity: "linkedin_facebook_url", value: formData.linkedinFacebookUrl });
      entities.push({ entity: "full_name", value: formData.fullName });
      entities.push({ entity: "email_address", value: formData.emailAddress });
      entities.push({ entity: "phone_number", value: formData.phoneNumber });
      entities.push({ entity: "accept_terms", value: formData.acceptTerms.toString() });
      entities.push({ entity: "collection_name", value: formData.legalBusinessName });
      portfolioId && entities.push({ entity: "portfolio_id", value: portfolioId });

      // Kick off the verification process workflow. this also needs to create the project and add it to the IR Dashboard portfolio
      track(CLAIM_COMPANY_FORM_VERIFICATION_STARTED, { userName: fullName, email });
      dispatch(
        sendMessage({
          conversationId: conversationId.current,
          intent: "/claim_company",
          entities,
        })
      );
      toast({
        render: ({ onClose }) => (
          <ToastMessageContent
            message={
              "We will start the verification of your business claim shortly. We will notify you once the process is complete or if we need more information."
            }
            onClose={onClose}
          />
        ),
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // TODO: Remove this simulation of processing time and replace with actual API call
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // TODO: this needs to route to the IR Dashboard portfolio page
      navigate("/dashboard");
    } catch (error) {
      console.error("Error submitting claim company form:", error);
      setSubmitError("Failed to submit business verification. Please try again.");
    } finally {
      setIsVerifying(false);
      setState((prev) => ({ ...prev, isVerifying: false }));
    }
  };

  // Function to handle form field changes and update step completion
  const handleFormChange = (formData: Partial<ClaimCompanyFormData>) => {
    // Check if business information is complete (step 2)
    const areBusinessFieldsComplete = !!(
      formData.legalBusinessName &&
      formData.country &&
      formData.streetAddress &&
      formData.provinceState &&
      formData.postalZip &&
      formData.registrationTaxId &&
      formData.websiteUrl &&
      formData.linkedinFacebookUrl &&
      formData.crunchbasePitchbookUrl
    );

    // Check if primary contact information is complete (step 3)
    const areContactFieldsComplete = !!(formData.fullName && formData.emailAddress && formData.phoneNumber);

    setState((prev) => ({
      ...prev,
      hasCompletedStep2: areBusinessFieldsComplete,
      hasCompletedStep3: areContactFieldsComplete,
    }));
  };

  return <ClaimCompanyForm onSubmit={handleSubmit} isVerifying={isVerifying} submitError={submitError} onFormChange={handleFormChange} />;
};
